import { FC, ReactNode, useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import { getUser } from '@frontend/auth-helpers';
import { Headers } from '@frontend/header';
import { Trans, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { Page } from '@frontend/page';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Chip, Heading, NakedButton, Text } from '@frontend/design-system';
import { SelfUpgradeActionType } from '../../constants/self-upgrade';
import {
  getFeaturePromoActionTrackingId,
  getFeatureWiseTrackingId,
  SelfUpgradeLandingPageTrackingIds,
} from '../../constants/tracking-ids';
import { useGetSelfUpgradeAction } from '../../hooks/useGetSelfUpgradeAction';
import { useFeatureSubscription } from '../../providers/FeatureSubscriptionProvider';
import { getBundleTrialDays, getScheduleDemoLink, getUpgradeBundleDisplayName } from '../../self-upgrade.util';
import { FeaturePromotionMode, FeaturePromotionPageBaseProps } from '../../types';

interface Props extends FeaturePromotionPageBaseProps {
  showPremiumBadge?: boolean;
  featureName?: string;
  title: string | ReactNode;
  description: string | ReactNode;
  media?: { src: string; alt: string };
  skipHeaderNav?: boolean;
}

export const FeaturePromotionBasePage: FC<Props> = ({
  featureName,
  showPremiumBadge,
  title,
  description,
  isDemoAvailable,
  media,
  mode = 'fullPage',
  skipHeaderNav,
  handleOpenUpgradeModal,
  handleViewDemo,
}) => {
  const { feature, isBundleUpgradeEligible, recommendedBundle } = useFeatureSubscription();
  const { actionType, actionIconName, actionTextData, isLoading } = useGetSelfUpgradeAction();
  const { t } = useTranslation('featurePromotion', { keyPrefix: 'landing-page-promo' });
  const email = getUser()?.username;

  const handlePromoActionClick = useCallback(() => {
    switch (actionType) {
      case SelfUpgradeActionType.SCHEDULE_CALL:
        window.open(getScheduleDemoLink(feature, email), '_blank');
        break;
      case SelfUpgradeActionType.REQUEST_UPGRADE:
      case SelfUpgradeActionType.UPGRADE_NOW:
      case SelfUpgradeActionType.FREE_TRIAL:
        handleOpenUpgradeModal();
        break;
      default:
        return;
    }
  }, [actionType, email, feature, handleOpenUpgradeModal]);

  const actionButtonTrackingId = useMemo<string | undefined>(() => {
    return getFeaturePromoActionTrackingId({
      feature,
      source: 'landing-page',
      action: actionType,
      upgradeType: isBundleUpgradeEligible ? 'bundle' : 'feature',
    });
  }, [actionType, feature, isBundleUpgradeEligible]);

  const { bundleName, bundleTrialDays } = useMemo(() => {
    if (!isBundleUpgradeEligible) return {};
    return {
      bundleName: getUpgradeBundleDisplayName(recommendedBundle),
      bundleTrialDays: getBundleTrialDays(recommendedBundle),
    };
  }, [isBundleUpgradeEligible, recommendedBundle]);

  const pageStyles = useMemo(() => getStyles(mode), [mode]);

  return (
    <Page css={pageStyles.page} data-testid='subscription-landing-page-promotion'>
      <Headers.Root css={pageStyles.pageHeader} size='sm'>
        {!skipHeaderNav && (
          <div css={pageStyles.pageHeaderBreadcrumb}>
            <Headers.RouterBreadcrumb />
          </div>
        )}
        <Headers.TitleRow>
          {mode === 'modal' || skipHeaderNav ? (
            <Headers.Title>
              <Heading level={2}>{title}</Heading>
            </Headers.Title>
          ) : (
            <Headers.Title>{typeof title === 'string' ? <Headers.RouterHeader title={title} /> : title}</Headers.Title>
          )}
        </Headers.TitleRow>
        {(showPremiumBadge || isBundleUpgradeEligible) && (
          <Chip variant='seaweed' css={{ maxWidth: 'max-content' }}>
            {t('Premium Feature')}
          </Chip>
        )}
      </Headers.Root>
      <Page.Body css={pageStyles.pageBody}>
        {typeof description === 'string' ? (
          <Text size={'large'} css={pageStyles.description}>
            {isBundleUpgradeEligible ? (
              <Trans t={t} count={bundleTrialDays} values={{ bundleName, bundleTrialDays, featureName }}>
                {{ featureName }} is included in the {{ bundleName }} Bundle along with other powerful features to
                improve office efficiency.
                <div>
                  Try {{ bundleName }} free for {{ bundleTrialDays }} days.
                </div>
              </Trans>
            ) : (
              description
            )}
          </Text>
        ) : (
          description
        )}

        <div css={pageStyles.actionBar}>
          {!isLoading && actionType !== SelfUpgradeActionType.NONE && (
            <>
              <NakedButton
                trackingId={actionButtonTrackingId}
                css={pageStyles.actionButton}
                onClick={handlePromoActionClick}
              >
                <Icon name={actionIconName} size={16} color='white' />
                <Text as='span' color='white' weight='bold'>
                  {actionTextData?.primaryButtonText}
                </Text>
              </NakedButton>
              {actionTextData?.actionInfoText && (
                <Text color='light' size='medium'>
                  {actionTextData?.actionInfoText}
                </Text>
              )}
            </>
          )}
          {isDemoAvailable && (
            <NakedButton
              trackingId={getFeatureWiseTrackingId(feature, SelfUpgradeLandingPageTrackingIds.viewDemoAction)}
              onClick={handleViewDemo}
            >
              <Text color='primary' weight='bold' size='medium'>
                {t('View Demo')}
              </Text>
            </NakedButton>
          )}
        </div>
        {!!media?.src && <img src={media.src} css={pageStyles.featureImage} alt={media?.alt} />}
      </Page.Body>
    </Page>
  );
};

const getStyles = (mode: FeaturePromotionMode) => {
  const applyResponsive = mode !== 'modal';

  return {
    page: css`
      padding: 0 !important;
    `,
    pageHeader: css`
      padding: ${theme.spacing(4, 4, 0, 4)};
      display: flex;
      flex-direction: column-reverse;
      align-items: center;

      ${applyResponsive &&
      `@container (max-width: ${breakpoints.xsmall.max}px) {
        padding: ${theme.spacing(2, 2, 0, 2)};
        align-items: flex-start;
        flex-direction: column;
      }`}
    `,
    pageHeaderBreadcrumb: css`
      display: none;

      ${applyResponsive &&
      `@container (max-width: ${breakpoints.xsmall.max}px) {
        display: block;
        margin-bottom: ${theme.spacing(1)};
      }`}
    `,
    pageBody: css`
      display: flex;
      flex-direction: column;
      padding: ${theme.spacing(1, 4, 4, 4)};
      gap: ${theme.spacing(3)};
      align-items: center;

      ${applyResponsive &&
      `@container (max-width: ${breakpoints.xsmall.max}px) {
        align-items: flex-start;
        padding: ${theme.spacing(2)};
        gap: ${theme.spacing(2)};
      }`}
    `,
    actionBar: css`
      display: flex;
      flex-direction: column;
      gap: ${theme.spacing(1)};

      ${applyResponsive &&
      `@container (max-width: ${breakpoints.xsmall.max}px) {
        flex-direction: row;
        align-items: center;
        gap: ${theme.spacing(2)};
      }`}
    `,
    description: css`
      max-width: 512px;
      text-align: center;

      ${applyResponsive &&
      `@container (max-width: ${breakpoints.xsmall.max}px) {
        text-align: left;
      }`}
    `,
    actionButton: css`
      background: ${theme.colors.primary50};
      height: ${theme.spacing(4)};
      padding: ${theme.spacing(0, 1.5)};
      display: flex;
      align-items: center;
      justify-content: center;
      gap: ${theme.spacing(0.5)};
      border-radius: ${theme.borderRadius.small};
    `,
    featureImage: css`
      height: auto;
      width: 700px;
      max-width: 100%;
      border-radius: ${theme.borderRadius.medium};
    `,
  };
};
